import React, { useState, useCallback, useEffect } from 'react';
import Autocomplete from '@mui/material/Autocomplete';
import { Checkbox } from '@mui/material';
import KogInputText from '../inputText/inputText';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import { KeyboardArrowDown } from '@mui/icons-material';
import { debounce } from 'lodash';
import { PaginationResponse } from '@/main/models/common/paginationResponse';
import { Spinner } from '../inputAutoComplete/styles';
import { PaginationEnum } from '@/main/services/enum/PaginationEnum';
import {
  getDefaultMessageResultsFound,
  getTranslateByKey
} from '@/main/services/utils';
import { useStyles, OptionTextLabel } from './styles';

const DEBOUNCE_MS = 600;

export interface PaginatedMultiSelectProps<T> {
  loadData: (
    searchTerm: string,
    pageIndex: number,
    pageSize: number
  ) => Promise<PaginationResponse<T>>;
  getOptionKey: (option: T) => string | number;
  getOptionLabel: (option: T) => string;
  onSelect: (selected: T[]) => void;
  value?: T[];
  label?: string;
  disabled?: boolean;
  required?: boolean;
  id?: string;
  reloadTrigger?: boolean;
}

interface ListboxExtraProps {
  textResultFound?: string;
  textIsLoading?: string;
  isLoading?: boolean;
}

const PaginatedListbox = React.forwardRef<
  HTMLDivElement,
  React.HTMLAttributes<HTMLElement> & ListboxExtraProps
>(({ children, textResultFound, textIsLoading, isLoading, ...rest }, ref) => (
  <div ref={ref}>
    {textResultFound && (
      <span
        style={{
          display: 'block',
          padding: '10px 0px 5px 16px',
          fontWeight: 'bold'
        }}>
        {textResultFound}
      </span>
    )}
    <ul
      {...rest}
      style={{
        ...(rest.style || {}),
        margin: 0,
        padding: '8px 0',
        listStyle: 'none',
        maxHeight: '40vh',
        overflowY: 'auto'
      }}>
      {children}
    </ul>
    {isLoading && (
      <span
        style={{
          display: 'block',
          padding: '10px 0px 10px 16px',
          fontWeight: 'bold'
        }}>
        {textIsLoading}
      </span>
    )}
  </div>
));

function KogPaginatedMultiSelectInner<T>(
  {
    loadData,
    getOptionKey,
    getOptionLabel,
    onSelect,
    value = [],
    label = '',
    disabled = false,
    required = false,
    id,
    reloadTrigger
  }: PaginatedMultiSelectProps<T>,
  ref: React.ForwardedRef<HTMLDivElement>
) {
  const classes = useStyles();

  const [options, setOptions] = useState<T[]>([]);
  const [totalItems, setTotalItems] = useState(0);
  const [searchTerm, setSearchTerm] = useState('');
  const [pageIndex, setPageIndex] = useState(PaginationEnum.PageIndexIntial);
  const [hasMore, setHasMore] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isLoadingMore, setIsLoadingMore] = useState(false);

  const fetchPage = useCallback(
    async (term: string, page: number, append: boolean) => {
      const setLoader = append ? setIsLoadingMore : setIsLoading;
      setLoader(true);

      try {
        const res = await loadData(term, page, PaginationEnum.PageSize10);
        const newData = res.results ?? [];

        setOptions(prev => (append ? [...prev, ...newData] : newData));
        setTotalItems(res.totalNumberOfRecords);
        setHasMore(res.pageNumber < res.totalNumberOfPages);
        setPageIndex(page);
      } finally {
        setLoader(false);
      }
    },
    [loadData]
  );

  useEffect(() => {
    fetchPage('', PaginationEnum.PageIndexIntial, false);
  }, [reloadTrigger]);

  const debouncedSearch = useCallback(
    debounce((term: string) => {
      setSearchTerm(term);
      fetchPage(term, PaginationEnum.PageIndexIntial, false);
    }, DEBOUNCE_MS),
    [fetchPage]
  );

  const handleScroll = (event: React.UIEvent<HTMLUListElement>) => {
    const el = event.currentTarget;
    const position = el.scrollTop + el.clientHeight;
    if (el.scrollHeight - position <= 1 && hasMore && !isLoadingMore) {
      fetchPage(searchTerm, pageIndex + PaginationEnum.PageIndexIntial, true);
    }
  };

  const loadingText = `${getTranslateByKey('information.searchingXnuupPartId')}...`;

  return (
    <Autocomplete<T, true>
      ref={ref}
      id={id}
      multiple
      disableCloseOnSelect
      disablePortal
      className={classes.inputAutoCompleteCheckbox}
      size="small"
      disabled={disabled}
      options={options}
      value={value}
      loading={isLoading}
      loadingText={loadingText}
      noOptionsText={getTranslateByKey('information.noRecordsFound')}
      popupIcon={isLoading ? <Spinner /> : <KeyboardArrowDown />}
      filterOptions={x => x}
      getOptionLabel={getOptionLabel}
      isOptionEqualToValue={(a, b) => getOptionKey(a) === getOptionKey(b)}
      ChipProps={{ classes: { root: classes.chip } }}
      limitTags={1}
      onChange={(_, newValue, reason) => {
        onSelect(reason === 'clear' ? [] : newValue);
      }}
      onInputChange={(_, newInputValue, reason) => {
        const isEmpty = newInputValue === '';

        if (reason === 'clear' || isEmpty) {
          debouncedSearch.cancel();
          setSearchTerm('');
          fetchPage('', PaginationEnum.PageIndexIntial, false);
        } else if (reason === 'input') {
          debouncedSearch(newInputValue);
        }
      }}
      slots={{
        listbox: PaginatedListbox as any
      }}
      slotProps={{
        listbox: {
          onScroll: handleScroll,
          textResultFound:
            !isLoading && options.length > 0
              ? getDefaultMessageResultsFound(options.length, totalItems)
              : '',
          textIsLoading: loadingText,
          isLoading: isLoadingMore
        } as any
      }}
      renderOption={(liProps, option, { selected }) => (
        <li
          {...liProps}
          className={`${liProps.className || ''} ${classes.listboxOption}`}
          style={{
            ...(liProps.style || {}),
            pointerEvents: isLoading ? 'none' : 'auto',
            opacity: isLoading ? 0.5 : 1
          }}>
          <Checkbox
            icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
            checkedIcon={<CheckBoxIcon fontSize="small" />}
            checked={selected}
            disabled={isLoading}
            style={{ marginRight: 8, flexShrink: 0 }}
          />
          <OptionTextLabel title={getOptionLabel(option)}>
            {getOptionLabel(option)}
          </OptionTextLabel>
        </li>
      )}
      renderInput={params => (
        <KogInputText
          {...params}
          label={label}
          required={required}
          variant="filled"
        />
      )}
    />
  );
}

const KogPaginatedMultiSelect = React.forwardRef(
  KogPaginatedMultiSelectInner
) as <T>(
  props: PaginatedMultiSelectProps<T> & { ref?: React.Ref<HTMLDivElement> }
) => React.ReactElement;

export default KogPaginatedMultiSelect;
